"use strict";
/**
 * APITable <https://github.com/apitable/apitable>
 * Copyright (C) 2022 APITable Ltd. <https://apitable.com>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.getAvInfoRequestUrl = exports.getDownloadSrc = exports.getCellValueThumbSrc = exports.isSupportImage = exports.showOriginImageThumbnail = exports.imageSizeExceeded = exports.renderFileIconUrl = exports.isDocType = exports.isWhatFileType = exports.DOC_MIME_TYPE = exports.NO_SUPPORT_IMG_MIME_TYPE = exports.DocType = exports.FileType = exports.browser = void 0;
const core_1 = require("@apitable/core");
const attr_accept_1 = __importDefault(require("attr-accept"));
const bowser_1 = __importDefault(require("bowser"));
const mime_types_1 = __importDefault(require("mime-types"));
const compressed_placeholder_png_1 = __importDefault(require("../../../../static/compressed_placeholder.png")); // zip
const excel_placeholder_png_1 = __importDefault(require("../../../../static/excel_placeholder.png")); // excel
const other_placeholder_png_1 = __importDefault(require("../../../../static/other_placeholder.png")); // other
const pdf_placeholder_png_1 = __importDefault(require("../../../../static/pdf_placeholder.png")); // pdf
const ppt_placeholder_png_1 = __importDefault(require("../../../../static/ppt_placeholder.png")); // ppt
const small_placeholder_filled_png_1 = __importDefault(require("../../../../static/small_placeholder_filled.png")); // img
const text_placeholder_png_1 = __importDefault(require("../../../../static/text_placeholder.png")); // txt
const video_placeholder_png_1 = __importDefault(require("../../../../static/video_placeholder.png")); // Multimedia
const word_placeholder_png_1 = __importDefault(require("../../../../static/word_placeholder.png")); // word icon
let _browser;
(() => {
    if (!process.env.SSR) {
        _browser = bowser_1.default.getParser(window.navigator.userAgent);
        window.bowser = _browser;
    }
})();
exports.browser = _browser;
function byte2Mb(byte) {
    if (!byte) {
        return 0;
    }
    return parseFloat((byte / 1024 / 1024).toFixed(2));
}
var FileType;
(function (FileType) {
    FileType[FileType["Other"] = 0] = "Other";
    FileType[FileType["Image"] = 1] = "Image";
    FileType[FileType["Doc"] = 2] = "Doc";
    FileType[FileType["Pdf"] = 3] = "Pdf";
    FileType[FileType["Media"] = 4] = "Media";
    FileType[FileType["Zip"] = 5] = "Zip";
    FileType[FileType["Txt"] = 6] = "Txt";
})(FileType = exports.FileType || (exports.FileType = {}));
var DocType;
(function (DocType) {
    DocType[DocType["Word"] = 0] = "Word";
    DocType[DocType["Excel"] = 1] = "Excel";
    DocType[DocType["PPT"] = 2] = "PPT";
})(DocType = exports.DocType || (exports.DocType = {}));
exports.NO_SUPPORT_IMG_MIME_TYPE = [
    'image/vnd.adobe.photoshop',
    'image/tiff',
];
const WORD_MIME_TYPE = [
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.template',
    'application/vnd.ms-word.document.macroEnabled.12',
];
const PPT_MIME_TYPE = [
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'application/vnd.openxmlformats-officedocument.presentationml.template',
    'application/vnd.openxmlformats-officedocument.presentationml.slideshow',
    'application/vnd.ms-powerpoint.addin.macroEnabled.12',
    'application/vnd.ms-powerpoint.presentation.macroEnabled.12',
    'application/vnd.ms-powerpoint.template.macroEnabled.12',
    'application/vnd.ms-powerpoint.slideshow.macroEnabled.12',
];
const EXCEL_MIME_TYPE = [
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.template',
    'application/vnd.ms-excel.sheet.macroEnabled.12',
    'application/vnd.ms-excel.template.macroEnabled.12',
    'application/vnd.ms-excel.addin.macroEnabled.12',
    'application/vnd.ms-excel.sheet.binary.macroEnabled.12',
    'text/csv',
];
exports.DOC_MIME_TYPE = [
    ...WORD_MIME_TYPE,
    ...EXCEL_MIME_TYPE,
    ...PPT_MIME_TYPE,
];
const MEDIA_TYPE = ['audio/*', 'video/*'];
// ts suffix files are recognized as video/mp2t video format by default.
const INVALID_MEDIA_TYPE = ['video/mp2t'];
const ZIPPED_TYPE = [
    'application/zip',
    'application/x-7z-compressed',
    'application/x-rar-compressed',
];
function isWhatFileType(file) {
    const inferredType = mime_types_1.default.lookup(file.name);
    if ((0, core_1.isImage)(file)) {
        return FileType.Image;
    }
    if ((0, core_1.isPdf)(file)) {
        return FileType.Pdf;
    }
    if ((0, attr_accept_1.default)(file, exports.DOC_MIME_TYPE) || inferredType && exports.DOC_MIME_TYPE.includes(inferredType)) {
        return FileType.Doc;
    }
    if ((0, attr_accept_1.default)(file, MEDIA_TYPE) && inferredType && !INVALID_MEDIA_TYPE.includes(inferredType)) {
        return FileType.Media;
    }
    if ((0, attr_accept_1.default)(file, ZIPPED_TYPE)) {
        return FileType.Zip;
    }
    if ((0, attr_accept_1.default)(file, 'text/plain')) {
        return FileType.Txt;
    }
    return FileType.Other;
}
exports.isWhatFileType = isWhatFileType;
function isDocType(file) {
    const inferredType = mime_types_1.default.lookup(file.name);
    if ((0, attr_accept_1.default)(file, WORD_MIME_TYPE) || inferredType && WORD_MIME_TYPE.includes(inferredType)) {
        return DocType.Word;
    }
    if ((0, attr_accept_1.default)(file, PPT_MIME_TYPE) || inferredType && PPT_MIME_TYPE.includes(inferredType)) {
        return DocType.PPT;
    }
    if ((0, attr_accept_1.default)(file, EXCEL_MIME_TYPE) || inferredType && EXCEL_MIME_TYPE.includes(inferredType)) {
        return DocType.Excel;
    }
    return '';
}
exports.isDocType = isDocType;
function renderFileIconUrl(curFile) {
    const type = isWhatFileType({ name: curFile.name, type: curFile.type });
    switch (type) {
        case FileType.Image: {
            return small_placeholder_filled_png_1.default;
        }
        case FileType.Media: {
            return video_placeholder_png_1.default;
        }
        case FileType.Doc: {
            const docType = isDocType(curFile);
            if (docType === DocType.Word) {
                return word_placeholder_png_1.default;
            }
            if (docType === DocType.Excel) {
                return excel_placeholder_png_1.default;
            }
            if (docType === DocType.PPT) {
                return ppt_placeholder_png_1.default;
            }
            return other_placeholder_png_1.default;
        }
        case FileType.Pdf: {
            return pdf_placeholder_png_1.default;
        }
        case FileType.Zip: {
            return compressed_placeholder_png_1.default;
        }
        case FileType.Txt: {
            return text_placeholder_png_1.default;
        }
        case FileType.Other:
        default: {
            return other_placeholder_png_1.default;
        }
    }
}
exports.renderFileIconUrl = renderFileIconUrl;
const imageSizeExceeded = (size) => {
    const MAX_FILE_SIZE = 20;
    return byte2Mb(size) >= MAX_FILE_SIZE;
};
exports.imageSizeExceeded = imageSizeExceeded;
/**
 * Whether to display thumbnails of the original image, e.g. not after the volume exceeds 20Mb.
 */
const showOriginImageThumbnail = (file) => {
    const fileArgument = { name: file.name, type: file.mimeType };
    return (((0, core_1.isPdf)(fileArgument) && file.preview) ||
        ((0, core_1.isImage)(fileArgument) &&
            !(0, exports.imageSizeExceeded)(file.size) &&
            (0, exports.isSupportImage)(file.mimeType)));
};
exports.showOriginImageThumbnail = showOriginImageThumbnail;
const isSupportImage = (mimeType) => {
    return !exports.NO_SUPPORT_IMG_MIME_TYPE.includes(mimeType);
};
exports.isSupportImage = isSupportImage;
// Get a preview of the file.
// 1. Pictures, PDF Show pictures.
// 2. Other file types show the corresponding Icon.
const getCellValueThumbSrc = (file, option) => {
    let imgSrc = '';
    if (!file) {
        return imgSrc;
    }
    if ((0, exports.showOriginImageThumbnail)(file)) {
        const transformWebpIfNeeded = ((0, core_1.isWebp)({ name: file.name, type: file.mimeType }) &&
            (exports.browser === null || exports.browser === void 0 ? void 0 : exports.browser.satisfies({
                safari: '<14',
            }))) ||
            (exports.browser === null || exports.browser === void 0 ? void 0 : exports.browser.is('iOS'));
        imgSrc = (0, core_1.cellValueToImageSrc)(file, Object.assign(Object.assign({}, option), { isPreview: true, formatToJPG: transformWebpIfNeeded ? true : option.formatToJPG }));
    }
    else {
        imgSrc = renderFileIconUrl({ name: file.name, type: file.mimeType });
    }
    return imgSrc;
};
exports.getCellValueThumbSrc = getCellValueThumbSrc;
function getDownloadSrc(fileInfo) {
    return `${core_1.Settings[fileInfo.bucket].value}${fileInfo.token}?attname=${encodeURIComponent(fileInfo.name)}`;
}
exports.getDownloadSrc = getDownloadSrc;
function getAvInfoRequestUrl(fileInfo) {
    return `${core_1.Settings[fileInfo.bucket].value}${fileInfo.token}?avinfo`;
}
exports.getAvInfoRequestUrl = getAvInfoRequestUrl;
